import type { Option } from '@de/types/types';

/**
 * Compose a DE {@link Option} from a single `(formKey, value)` form-state
 * commit plus the resolved display label and the lead-field association
 * carried on the component's `_meta.leadFieldAssociated`.
 *
 * Per child SAD ADR-15 the canonical write shape is:
 *   `{ optionId: <formKey>, text: <label>, value: <bound field>,
 *      slug?: <slug>, leadFieldAssociated?: <fromMeta> }`
 *
 * `optionId` mirrors `formKey` so downstream branching logic (which
 * matches on `optionId` for `OPTION_SELECTED`) and the lead-capture
 * pipeline (which keys on the same identifier) line up. `value`
 * carries whatever the DSL author bound via `_bind.optionValueField`
 * — that's the field cascading data sources interpolate into their
 * templated bodies (Mobius criteria want a numeric id, search filters
 * may want a slug, etc.), so it has to faithfully echo the author's
 * choice. `slug` is the parallel canonical-identity slot used by
 * `COMBINATION_SELECTED` branching conditions; the bridge fills it
 * from the bound source's `slug` field when one exists so authors
 * never need to encode slugs into `_bind.optionValueField` just to
 * make branching resolve.
 */
export function buildOptionFromFormState(input: {
  formKey: string;
  value: string;
  label: string;
  slug?: string;
  leadFieldAssociated?: string;
  customField?: string;
  /**
   * When true, `buildFlowContext` expands a CSV `value` into a flat
   * `string[]` on step-id / `customField` context keys (DSL Select
   * `_meta.isArray` parity with Flow Wizard option `isArray`).
   */
  isArray?: boolean;
}): Option {
  const option: Option = {
    optionId: input.formKey,
    text: input.label,
    value: input.value,
  };
  if (input.slug) {
    option.slug = input.slug;
  }
  if (input.leadFieldAssociated) {
    option.leadFieldAssociated = input.leadFieldAssociated;
  }
  if (input.customField) {
    option.customField = input.customField;
  }
  if (input.isArray === true) {
    option.isArray = true;
  }
  return option;
}
